import { externalData } from '@amazon/vinyl-observable'
import type { Territory } from '@amzn/vinyl-amzn'
import { ctx } from './util/context'
import { globalRef, logDebug, noop } from '@amazon/vinyl-util'
import { getLocale } from '@amzn/amp-i18n'

const { target, errorHandler } = ctx('marketplace')

/**
 * The customer's marketplace, expressed as the music territory country code
 * and the retail marketplace id. Field names mirror `IdentityModel` /
 * `CustomerAccountInfo` so callsites can swap a stratus-sourced read for
 * {@link getMarketplace} without renaming.
 */
export type MarketplaceInfo = {
    readonly musicTerritory: Territory
    readonly marketplaceId: string
}

const DEFAULT_TERRITORY: Territory = 'US'
const US_MARKETPLACE_ID = 'ATVPDKIKX0DER'

// Amazon TLD -> music territory, mirroring Green Hornet's getCurrentMarket()
// (HOST_TO_MARKET / MARKETS in DomainConstants.ts); GH's `UK` market is the ISO
// code `GB`. Every GH host resolves by its amazon TLD, so the TLD is the
// generalized key behind GH's per-host table.
const TLD_TERRITORY: Record<string, Territory> = {
    com: 'US',
    ca: 'CA',
    'com.br': 'BR',
    'com.mx': 'MX',
    'co.uk': 'GB',
    de: 'DE',
    fr: 'FR',
    it: 'IT',
    es: 'ES',
    in: 'IN',
    'co.jp': 'JP',
    'com.au': 'AU',
}

const TERRITORY_TLD: Record<string, string> = Object.fromEntries(
    Object.entries(TLD_TERRITORY).map(([tld, territory]) => [territory, tld])
)

/**
 * The territory's retail host (`www.amazon.co.uk` for GB), mirroring GH's
 * getRetailDomain(); unknown territories fall back to the US host.
 */
export function retailDomain(territory: Territory): string {
    return `www.amazon.${TERRITORY_TLD[territory] ?? 'com'}`
}

/** A page on the territory's retail site, carrying the UI locale the way GH's cookie links do. */
export function retailUrl(territory: Territory, path: string): string {
    const url = new URL(path, `https://${retailDomain(territory)}`)
    url.searchParams.append('locale', getLocale().value.replace('-', '_'))
    return url.toString()
}

export function cookiePreferencesUrl(territory: Territory): string {
    return retailUrl(territory, '/privacyprefs/music')
}

// Retail (obfuscated) marketplace id per territory — the value GH's config.json
// returns for each market's domain. Keeps marketplaceId consistent with the
// domain-resolved territory (DMLS authorizes against the pair).
const TERRITORY_MARKETPLACE_ID: Record<string, string> = {
    US: US_MARKETPLACE_ID,
    CA: 'A2EUQ1WTGCTBG2',
    BR: 'A2Q3Y263D00KWC',
    MX: 'A1AM78C64UM0Y8',
    GB: 'A1F83G8C2ARO7P',
    DE: 'A1PA6795UKMFR9',
    FR: 'A13V1IB3VIYZZH',
    IT: 'APJ6JRA9NG5V4',
    ES: 'A1RKKUPIHCS9HS',
    IN: 'A21TJRUUN4KGV1',
    JP: 'A1VC38T7YXB528',
    AU: 'A39IBJ37TRP1C6',
}

// GH's TLD_MATCH_REGEX (getCurrentMarket.ts): the amazon TLD suffixes GH maps to
// a market. A non-amazon host (localhost, *.amazon.dev) has no match -> US.
const TLD_MATCH = /\.amazon\.(com|ca|com\.br|com\.mx|co\.uk|de|fr|it|es|in|co\.jp|com\.au)$/

/**
 * Resolve the music territory from a page hostname, replicating GH's
 * domain-derived {@link getCurrentMarket}. Defaults to US off an amazon domain.
 */
export function territoryFromHostname(hostname: string): Territory {
    const tld = hostname.match(TLD_MATCH)?.[1] ?? 'com'
    return TLD_TERRITORY[tld]
}

// Domain-derived and synchronous, matching GH's getCurrentMarket(): the market
// is the amazon TLD of the page host; off-web (no `location`) falls back to US.
export function currentTerritory(): Territory {
    return typeof location !== 'undefined'
        ? territoryFromHostname(location.hostname)
        : DEFAULT_TERRITORY
}

function resolveMarketplace(): MarketplaceInfo {
    const musicTerritory = currentTerritory()
    return {
        musicTerritory,
        marketplaceId: TERRITORY_MARKETPLACE_ID[musicTerritory],
    }
}

const marketplaceRef = globalRef<{ promise: Promise<MarketplaceInfo> | undefined }>(() => ({
    promise: undefined,
}))

/**
 * Resolves the current marketplace. Memoized: resolved once from the page
 * domain and the same promise is returned on every call.
 */
export function getMarketplace(): Promise<MarketplaceInfo> {
    if (!marketplaceRef.value.promise) {
        const marketplace = resolveMarketplace()
        logDebug(target, `marketplace resolved to ${marketplace.musicTerritory}`)
        marketplaceRef.value.promise = Promise.resolve(marketplace)
    }
    return marketplaceRef.value.promise
}

/**
 * Observable for the current marketplace.
 */
export const marketplace$ = externalData<MarketplaceInfo | null>(null, (setData) => {
    getMarketplace().then(setData).catch(errorHandler)
    return noop
})
